import { DateTime } from "luxon";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "../../../../contexts/AuthProvider";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import { useRoleColors } from "../../../../hooks/useRoleColors";
import {
	getNurseEmploymentDropdownItems,
	getNurseRolesDropdownItems,
	type NurseEmploymentTypeKeys,
	type NurseFullRoleKeys,
} from "../../../../pages/Profile/v2/ProfessionalInfo/Dropdowns";
import { trackEvent } from "../../../../utils/analytics";
import { isNurseActiveAnywhere, isNurseActiveInDepartment } from "../../../../utils/formatters";
import { EmploymentType, type HospitalData, type Nurse, NurseFullRole, type NurseInShift } from "../../../../utils/models";
import { inverseRolesMap } from "../../../../utils/utilFunctions";
import type { DropdownItem } from "../../../UIToolkit/Inputs/SystemDropDown";
import { getSmsMaxChars, maxCharsDefault } from "../CharCounter";
import { usePostFacilityAnnouncement, usePostStaffMessage } from "../hooks";
import { type MessageFiltersType, MessageWorkingFilterEnum } from "./MessageFilter";
import type { NewBroadcastMessageModalProps } from "./NewBroadcastMessageModal";
import { type AnnouncementFilters, useFacilityAnnouncementScope } from "./useFacilityAnnouncementScope";

/** A recipient must hold one of the selected roles and, while a job filter is active, one of the selected jobs. */
export const matchesBroadcastRoleOrJob = (nurse: Nurse, selectedRoles: NurseFullRoleKeys[], activeJobIds: string[]): boolean => {
	const matchesRole = selectedRoles.some((role) => inverseRolesMap[nurse.role] === role);
	if (activeJobIds.length === 0) return matchesRole;
	return matchesRole && !!nurse.jobIds?.some((nurseJobId) => activeJobIds.includes(nurseJobId));
};

/**
 * Role options the composer offers. Opened from a shift posting (a job plus the shift's roles), the Role
 * filter lists only the roles that shift is posted for, so an admin can narrow within them but cannot widen
 * the pool to roles that can't claim the shift. Everywhere else every department role is offered.
 */
export const restrictBroadcastRoleOptions = <T extends DropdownItem<NurseFullRoleKeys>>(
	departmentRoles: T[],
	jobId: string | undefined,
	shiftRoles: NurseFullRole[] | undefined,
): T[] => {
	if (!jobId || !shiftRoles || shiftRoles.length === 0) return departmentRoles;
	return departmentRoles.filter((role) => shiftRoles.includes(NurseFullRole[role.value]));
};

// Referentially stable "no job filter" value so the recipients memo doesn't recompute every render.
const NO_JOB_FILTER: string[] = [];

const debugNurseInShifts = (currentNurseShifts: NurseInShift[]) => {
	/*Dont remove this - this log is handy for debugging filters*/
	console.log(
		currentNurseShifts.map(
			(value) =>
				`${value.nurseId} ${value.role} ${value.startTime.toFormat("d-MM-yyyy")} ${value.startTime.toFormat("HH:mm")} - ${value.endTime.toFormat("HH:mm")}\n`,
		),
	);
};

const debugNurseInShiftNurseIds = (relevantNurseIds: number[], hospitalData: HospitalData) => {
	/*Dont remove this - this log is handy for debugging filters*/
	console.log(relevantNurseIds);
	console.log(hospitalData.nurses.filter((value) => relevantNurseIds.includes(value.id) && isNurseActiveAnywhere(value) && value.userId));
};

export const useBroadcastMessageState = ({
	onClose,
	initialRolesToSelect,
	nurseIdsToIgnore,
	initialFilters,
	defaultWorkingFilter = MessageWorkingFilterEnum.STAFF_THAT_WORKS,
	initialSmsMessage,
	addAppLink,
	initialSelectedNurseIds,
	lockRecipientSelection,
	jobId,
	allowFacilityScope,
}: NewBroadcastMessageModalProps) => {
	const { departmentNurseTypes, isSnf, hospitalData, chosenDepartment, showErrorSnackbar, timezone } = useHospitalData();
	const formatPersonName = useFormatPersonName();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const availableJobs = useMemo<DropdownItem<string>[]>(
		// DEFAULT: departments without job management have no jobs list — empty means the Jobs filter simply doesn't render.
		() => (chosenDepartment?.jobs ?? []).map((job) => ({ value: job.id, label: job.displayName, id: job.id })),
		[chosenDepartment],
	);
	// Pickup-context broadcasts seed the job filter with the request's job; generic broadcasts start inactive.
	const [selectedJobIds, setSelectedJobIds] = useState<string[]>(() => (jobId ? [jobId] : []));
	const handleJobsChange = useCallback((items?: string[]) => {
		setSelectedJobIds(items ?? []);
	}, []);
	const activeJobIds = jobManagementEnabled && selectedJobIds.length > 0 ? selectedJobIds : NO_JOB_FILTER;
	const showJobsFilter = jobManagementEnabled && availableJobs.length > 0;
	const jobFilterActive = activeJobIds.length > 0;
	const [filters, setFilters] = useState<{ id: string; filterData: MessageFiltersType }[]>(() =>
		initialFilters
			? initialFilters.map((value) => ({
					id: uuidv4(),
					filterData: value,
				}))
			: [],
	);
	const nurseEmploymentDropdownItems = getNurseEmploymentDropdownItems(isSnf);
	const { userInfo } = useAuth();
	const roleColor = useRoleColors();
	const availableRoles = useMemo(() => {
		const departmentRoles = getNurseRolesDropdownItems(chosenDepartment, roleColor).filter((value) =>
			departmentNurseTypes.includes(NurseFullRole[value.value]),
		);
		return restrictBroadcastRoleOptions(departmentRoles, jobId, initialRolesToSelect);
	}, [departmentNurseTypes, chosenDepartment, roleColor, jobId, initialRolesToSelect]);

	const [selectedRoles, setSelectedRoles] = useState<NurseFullRoleKeys[]>(() =>
		initialRolesToSelect
			? availableRoles.filter((r) => initialRolesToSelect.includes(NurseFullRole[r.value])).map((r) => r.value)
			: availableRoles.map((r) => r.value),
	);
	const handleRolesChange = useCallback((items?: NurseFullRoleKeys[]) => {
		setSelectedRoles(items ?? []);
	}, []);
	const [selectedEmploymentTypes, setSelectedTypes] = useState<NurseEmploymentTypeKeys[]>(nurseEmploymentDropdownItems.map((value) => value.value));
	const handleEmploymentTypesChange = useCallback((items?: NurseEmploymentTypeKeys[]) => {
		setSelectedTypes(items ?? []);
	}, []);

	// The same choice, as an announcement to the building sends it: a filter with everything ticked doesn't
	// narrow (null), so it also reaches roles this department doesn't list and staff in no department.
	const announcementFilters = useMemo<AnnouncementFilters>(
		() => ({
			roles: selectedRoles.length === availableRoles.length ? null : selectedRoles.map((role) => NurseFullRole[role]),
			jobIds: activeJobIds.length > 0 ? activeJobIds : null,
			employmentTypes:
				selectedEmploymentTypes.length === nurseEmploymentDropdownItems.length ? null : selectedEmploymentTypes.map((type) => EmploymentType[type]),
		}),
		[selectedRoles, availableRoles.length, activeJobIds, selectedEmploymentTypes, nurseEmploymentDropdownItems.length],
	);
	const facilityScope = useFacilityAnnouncementScope(allowFacilityScope, announcementFilters);
	const { isFacilityAnnouncement } = facilityScope;

	const [selectedNurseList, setSelectedNurseList] = useState<Nurse[]>([]);
	const nursesOptions = useMemo(() => {
		const relevantNurseIds: number[] = [];
		if (filters.length > 0) {
			const nurseInShiftsForDates = hospitalData.weeks.flatMap<NurseInShift>((week) =>
				week.weeklyShifts.flatMap((weeklyShift) =>
					weeklyShift.days
						.filter((day) => filters.some((currFilter) => currFilter.filterData.dateFilter.hasSame(day.date, "day")))
						.flatMap((day) => day.nurses),
				),
			);
			const nurseIds = new Set(hospitalData.nurses.map((nurse) => nurse.id));
			nurseIds.forEach((currentNurseId) => {
				const currentNurseShifts = nurseInShiftsForDates.filter((value) => value.nurseId === currentNurseId);
				if (
					filters.every((checkedFilter) => {
						const relevantShifts = currentNurseShifts.filter((shift) => {
							return shift.startTime.hasSame(checkedFilter.filterData.dateFilter, "day");
						});
						return (
							(relevantShifts.length === 0 && checkedFilter.filterData.workFilter === MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK) ||
							(relevantShifts.length > 0 &&
								relevantShifts.every((checkedShift) => {
									const nurseStart = checkedShift.startTime;
									const nurseEnd = checkedShift.endTime.plus({ days: checkedShift.endTime <= checkedShift.startTime ? 1 : 0 });

									const filterStart = checkedFilter.filterData.timeFilter.start.set({
										year: checkedFilter.filterData.dateFilter.year,
										month: checkedFilter.filterData.dateFilter.month,
										day: checkedFilter.filterData.dateFilter.day,
									});

									const filterEndCalc = checkedFilter.filterData.timeFilter.end.set({
										year: checkedFilter.filterData.dateFilter.year,
										month: checkedFilter.filterData.dateFilter.month,
										day: checkedFilter.filterData.dateFilter.day,
									});

									const filterEnd = filterEndCalc <= filterStart ? filterEndCalc.plus({ days: 1 }) : filterEndCalc;

									const isNurseTimesInFilterRange = nurseStart < filterEnd && nurseEnd > filterStart;
									return checkedFilter.filterData.workFilter === MessageWorkingFilterEnum.STAFF_THAT_WORKS
										? isNurseTimesInFilterRange
										: !isNurseTimesInFilterRange;
								}))
						);
					})
				) {
					relevantNurseIds.push(currentNurseId);
					// debugNurseInShifts(currentNurseShifts);
				}
			});
			// debugNurseInShiftNurseIds(relevantNurseIds, hospitalData);
		}
		return hospitalData.nurses
			.filter(
				(value) =>
					chosenDepartment &&
					isNurseActiveInDepartment(value, chosenDepartment.department_id) &&
					!!value.userId &&
					!!value.phoneNumber &&
					matchesBroadcastRoleOrJob(value, selectedRoles, activeJobIds) &&
					!!selectedEmploymentTypes.find((empType) => value.employmentType === EmploymentType[empType]) &&
					(filters.length > 0 ? relevantNurseIds.includes(value.id) : true) &&
					((nurseIdsToIgnore?.length ?? 0) > 0 ? !nurseIdsToIgnore?.includes(value.id) : true),
			)
			.sort((a, b) => formatPersonName(a).localeCompare(formatPersonName(b)))
			.map<DropdownItem<Nurse>>((value) => ({
				value: value,
				label: () => formatPersonName(value),
				id: value.id,
			}));
	}, [
		hospitalData.nurses,
		selectedRoles,
		selectedEmploymentTypes,
		filters,
		hospitalData.weeks,
		nurseIdsToIgnore,
		chosenDepartment,
		activeJobIds,
		formatPersonName,
	]);

	useEffect(() => {
		const baseSelectable = nursesOptions.map((value) => value.value).filter((n) => n.userId && n.phoneNumber);
		if (initialSelectedNurseIds && initialSelectedNurseIds.length > 0) {
			const idSet = new Set(initialSelectedNurseIds);
			setSelectedNurseList(baseSelectable.filter((n) => idSet.has(n.id)));
			return;
		}
		setSelectedNurseList(baseSelectable);
	}, [nursesOptions, initialSelectedNurseIds]);
	const onDropdownSelectionChange = useCallback(
		(selectedValues: Nurse[]) => {
			if (lockRecipientSelection) return;
			setSelectedNurseList(selectedValues.filter((n) => n.userId && n.phoneNumber));
		},
		[lockRecipientSelection],
	);

	const [messageValue, setMessageValue] = useState<string>(initialSmsMessage ?? "");
	const handleMessageChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
		if (event.target.value.length > maxCharsDefault) {
			return;
		}
		setMessageValue(event.target.value);
	}, []);

	const [showBackWarning, setShowBackWarning] = useState<boolean>(false);
	const toggleBackWarning = useCallback(() => {
		setShowBackWarning((prev) => !prev);
	}, []);

	const handleCancelOrEscape = useCallback(() => {
		if (messageValue.trim().length === 0) {
			onClose();
		} else {
			toggleBackWarning();
		}
	}, [messageValue, onClose, toggleBackWarning]);

	const { mutateAsync: postBroadcastMessage, isPending: isPostingToStaff, isSuccess: isPostedToStaff } = usePostStaffMessage();
	const { mutate: postFacilityAnnouncement, isPending: isAnnouncing, isSuccess: isAnnounced } = usePostFacilityAnnouncement();
	const isLoading = isPostingToStaff || isAnnouncing;
	const isSuccess = isPostedToStaff || isAnnounced;

	const sendingDisabled = useMemo(() => {
		const hasRecipients = isFacilityAnnouncement ? facilityScope.hasRecipients : selectedNurseList.length > 0;
		return messageValue.trim().length === 0 || !hasRecipients || messageValue.length > getSmsMaxChars(messageValue);
	}, [messageValue, selectedNurseList.length, isFacilityAnnouncement, facilityScope.hasRecipients]);

	const handleSendMessage = useCallback(async () => {
		if (sendingDisabled) {
			showErrorSnackbar("Please enter a message and select at least one nurse.", {}, {});
			return;
		}
		if (isFacilityAnnouncement) {
			// The hook reports the outcome either way; the composer closes only once the texts went out.
			postFacilityAnnouncement({ message: messageValue.trim(), audience: facilityScope.audience }, { onSuccess: () => onClose(true) });
			return;
		}
		await postBroadcastMessage({
			message: messageValue.trim(),
			userIds: selectedNurseList.map((nurse) => nurse.userId).filter((res) => res !== undefined),
			tempId: uuidv4(),
			addAppLink,
		});
		trackEvent("broadcast_message_sent", {
			number_of_recipients: selectedNurseList.length,
			message: messageValue,
		});
		// `true` = a message actually went out. Callers that record something on the strength of a sent
		// message (a Move request, say) must not record it when the composer is merely dismissed.
		onClose(true);
	}, [
		sendingDisabled,
		messageValue,
		selectedNurseList,
		postBroadcastMessage,
		showErrorSnackbar,
		onClose,
		addAppLink,
		isFacilityAnnouncement,
		postFacilityAnnouncement,
		facilityScope.audience,
	]);

	const addFilterClicked = useCallback(() => {
		setFilters((prev) => [
			...prev,
			{
				id: uuidv4(),
				filterData: {
					workFilter: defaultWorkingFilter,
					dateFilter: DateTime.now().setZone(timezone),
					timeFilter: {
						start: DateTime.now().setZone(timezone).startOf("day").set({ hour: 6 }),
						end: DateTime.now().setZone(timezone).startOf("day").set({ hour: 6 }).plus({ days: 1 }).minus({ minutes: 1 }),
					},
				},
			},
		]);
	}, [timezone, defaultWorkingFilter]);

	const deleteFilterClicked = useCallback((id: string) => {
		setFilters((prev) => prev.filter((filterAndId) => filterAndId.id !== id));
	}, []);

	const handleFilterChanged = useCallback((filters: MessageFiltersType, filterUuid: string) => {
		setFilters((prev) => {
			const index = prev.findIndex((filterAndId) => filterAndId.id === filterUuid);
			if (index !== -1) {
				const newFilters = [...prev];
				newFilters[index] = { id: filterUuid, filterData: filters };
				return newFilters;
			}
			return prev;
		});
	}, []);

	return {
		filters,
		nurseEmploymentDropdownItems,
		selectedRoles,
		handleRolesChange,
		selectedEmploymentTypes,
		handleEmploymentTypesChange,
		selectedNurseList,
		nursesOptions,
		onDropdownSelectionChange,
		messageValue,
		handleMessageChange,
		showBackWarning,
		toggleBackWarning,
		handleCancelOrEscape,
		isLoading,
		sendingDisabled,
		handleSendMessage,
		addFilterClicked,
		deleteFilterClicked,
		handleFilterChanged,
		availableRoles,
		isSuccess,
		availableJobs,
		selectedJobIds,
		handleJobsChange,
		showJobsFilter,
		jobFilterActive,
		facilityScope,
	};
};
